import type Immutable from 'immutable'

import basketConfig from 'config/basket'

type Basket = {
  products: Record<string, number>
  gifts: Record<string, number>
}

export const getAllBasketProducts = (basket: Basket): Record<string, number> =>
  Object.entries(basket.products).reduce((acc: Record<string, number>, [productId, productQty]) => {
    const giftProductQty = basket.gifts[productId] ?? 0

    return {
      ...acc,
      [productId]: giftProductQty + productQty,
    }
  }, basket.gifts)

type Product = {
  categories: {
    id: string
  }[]
  boxLimit?: number
}

export const getProductsQtyInCategory = (
  categoryId: string,
  basket: Basket,
  products: Record<string, Product>,
  includeGiftProducts = true,
): number => {
  const productQtys = includeGiftProducts ? getAllBasketProducts(basket) : basket.products

  return Object.entries(productQtys).reduce((acc: number, [productId, itemQty]) => {
    const categoryFound = (products[productId]?.categories || []).some(
      (category) => category.id === categoryId,
    )

    return acc + (categoryFound ? itemQty : 0)
  }, 0)
}

type ProductCategory = {
  id: string
  title: string
  boxLimit?: number | string
}

export const getFirstProductCategoryAtLimit = (
  productId: string,
  basket: Basket,
  products: Record<string, Product>,
  productCategories: Record<string, ProductCategory>,
  includeGiftProducts = true,
): false | string => {
  let categoryAtLimit: false | string = false
  const itemCategories = products[productId]?.categories || []

  itemCategories.some((category) => {
    if (typeof category === 'undefined') {
      return false
    }

    const categoryLimit = Number(productCategories[category.id]?.boxLimit ?? 0)
    const qtyInCategory = getProductsQtyInCategory(
      category.id,
      basket,
      products,
      includeGiftProducts,
    )

    if (qtyInCategory >= categoryLimit) {
      categoryAtLimit = productCategories[category.id]?.title || false

      return true
    }

    return false
  })

  return categoryAtLimit
}

export const getProductItemLimitReached = (
  productId: string,
  basket: Basket,
  products: Record<string, Product>,
  includeGiftProducts = true,
): false | number => {
  const productItemLimit = Number(products[productId]?.boxLimit ?? 0)
  let totalProductQty = basket.products[productId] ?? 0
  if (includeGiftProducts) {
    totalProductQty += basket.gifts[productId] ?? 0
  }

  return totalProductQty >= productItemLimit ? productItemLimit : false
}

export const productsOverallLimitReached = (
  basket: Basket,
  includeGiftProducts = true,
): boolean => {
  let boxSum = Object.values(basket.products).reduce((sum, productQty) => sum + productQty, 0)
  if (includeGiftProducts) {
    boxSum += Object.values(basket.gifts).reduce((sum, giftQty) => sum + giftQty, 0)
  }

  return boxSum >= basketConfig.maxProductsNum
}

export const getProductLimitReached = (
  productId: string,
  immutableBasket: Immutable.Map<string, unknown>,
  immutableProducts: Immutable.Map<string, unknown>,
  immutableProductsCategories: Immutable.Map<string, unknown>,
):
  | false
  | { type: 'category'; value: string }
  | { type: 'box'; value: number }
  | { type: 'item'; value: number } => {
  const basket = immutableBasket.toJS() as Basket
  const products = immutableProducts.toJS() as Record<string, Product>
  const productCategories = immutableProductsCategories.toJS() as Record<string, ProductCategory>

  const overallLimitReached = productsOverallLimitReached(basket)
  if (overallLimitReached) {
    return { type: 'box', value: basketConfig.maxProductsNum }
  }

  const productItemAtLimit = getProductItemLimitReached(productId, basket, products)
  if (productItemAtLimit) {
    return { type: 'item', value: productItemAtLimit }
  }

  const categoryAtLimit = getFirstProductCategoryAtLimit(
    productId,
    basket,
    products,
    productCategories,
  )
  if (categoryAtLimit) {
    return { type: 'category', value: categoryAtLimit }
  }

  return false
}

export const productCanBeAdded = (
  productId: string,
  basket: Immutable.Map<string, unknown>,
  products: Immutable.Map<string, unknown>,
  productsStock: Immutable.Map<string, number>,
  productsCategories: Immutable.Map<string, unknown>,
): boolean =>
  productsStock.get(productId, 0) !== 0 &&
  !getProductLimitReached(productId, basket, products, productsCategories)
